import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "antd";
import classNames from "classnames";
import { deburr, get, isNil } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ACTION_CATALOG,
  SCREEN_COLLECTIONS,
} from "utils/api/graphql/queries/screens";
import CanvasIcon from "./CanvasIcon";
import { withRequesterVariables } from "./catalog";
import { collectionLabel, recommendedCenterPatch } from "./guide-choices";
import {
  FIELD_FILTERS,
  fieldGroups,
  groupTitle,
  PILL_TONES,
  rowPill,
  rowSummary,
  technicalPath,
} from "./screen-fields";
import { GUIDE_KINDS, GUIDE_STEPS } from "./screen-groups";
import { registryFieldOf } from "./slot-editing";
import { ICON_PATHS } from "./step-look";
import WorkshopGuide from "./WorkshopGuide";
import WorkshopQuickEdit from "./WorkshopQuickEdit";
import { SampleAnswer } from "./WorkshopSlotEditors";
import usePanelMaxHeight from "./use-panel-max-height";
import { answersByStep, answerText } from "./workshop-preview";
import {
  fillTemplate,
  REPORT_LEVELS,
  registryActions,
  SLOT_ICONS,
  SLOT_TYPES,
  slotKindOf,
  slotLabel,
  stateSwitchesOf,
  WORKSHOP_ICONS,
} from "./workshop-view";

const SECTIONS = { REPORT: "section:report", ORPHANS: "section:orphans" };

const FOCUS = { SEARCH: "search", BACK: "back" };

const guideKeyOf = (guide) => `guide:${guide.kind}:${guide.path.join(".")}`;

const QUICK_TYPES = [
  SLOT_TYPES.TEXT,
  SLOT_TYPES.IMAGE,
  SLOT_TYPES.INPUT,
  SLOT_TYPES.ACTION,
];

const GUIDE_ICONS = {
  [GUIDE_KINDS.LIST]: WORKSHOP_ICONS.list,
  [GUIDE_KINDS.CARD]: WORKSHOP_ICONS.screen,
};

const normalized = (text) => deburr(String(text || "")).toLowerCase();

const matches = (entry, query) => {
  const wanted = normalized(query).trim();
  if (!wanted) return true;
  return [entry.label, entry.scope, entry.summary, entry.pill.text, entry.path]
    .map(normalized)
    .some((text) => text.includes(wanted));
};

const reportEntryText = (entry) =>
  [get(entry, "dataField"), get(entry, "reason") || get(entry, "note")]
    .filter(Boolean)
    .join(" — ");

const FieldSummary = ({ entry: { icon, label, scope, summary, pill } }) => (
  <>
    <span
      className={`workshop-field-list__icon workshop-field-list__icon--${pill.tone}`}
    >
      <CanvasIcon path={icon} size={16} strokeWidth={1.9} />
    </span>
    <span className="workshop-field-list__text">
      <span className="workshop-field-list__label">
        {label}
        {scope ? (
          <span className="workshop-field-list__scope">{scope}</span>
        ) : null}
      </span>
      <span className="workshop-field-list__summary">{summary}</span>
    </span>
    <span
      className={`workshop-field-list__pill workshop-field-list__pill--${pill.tone}`}
    >
      {pill.text}
    </span>
  </>
);

FieldSummary.propTypes = {
  entry: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    label: PropTypes.string,
    scope: PropTypes.string,
    summary: PropTypes.string,
    pill: PropTypes.shape({ tone: PropTypes.string, text: PropTypes.string })
      .isRequired,
  }).isRequired,
};

const ScreenFields = ({
  workshop,
  variables,
  report,
  focusSearch,
  onClose,
  embedded,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const WORKSHOP = t("SCREEN_WORKSHOP", { returnObjects: true });
  const CONDITIONS =
    t("FORM.POSSIBLE_STATUSES.CONDITIONS", { returnObjects: true }) || {};
  const { FIELDS, FIELD_LIST: LIST, VIEWS, GUIDE, QUICK } = WORKSHOP;
  const CHECK = FIELDS.CHECK;
  const { fields, answers, sample, locked } = workshop;
  const [query, setQuery] = useState("");
  const [focusWanted, setFocusWanted] = useState(
    focusSearch ? FOCUS.SEARCH : null,
  );
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const backRef = useRef(null);
  const closeRef = useRef(null);
  const openedFromList = useRef(null);
  const { data: catalogData } = useQuery(ACTION_CATALOG);
  const { data: collectionsData } = useQuery(SCREEN_COLLECTIONS);
  const collections = get(collectionsData, "screenCollections") || [];
  const actions = useMemo(
    () => registryActions(get(catalogData, "actionCatalog") || []),
    [catalogData],
  );
  const { rows, problems, known } = fields;
  const { orphans } = answers;
  const fixing = fields.filter === FIELD_FILTERS.FIX;
  const searching = query.trim().length > 0;
  const flagged = rows.filter((row) => !isNil(row.problem));
  const fixCount = flagged.length + problems.length + orphans.length;
  const total = rows.length + problems.length + orphans.length;
  const manual = get(report, "manual") || [];
  const fixPill = { tone: PILL_TONES.FIX, text: LIST.PILLS.FIX };
  const summaryContext = {
    labels: LIST,
    fieldLabels: FIELDS,
    ruleLabels: CHECK.RULES,
    known,
    formDefinition: answers.formDefinition,
  };
  const sampleFields = sample.fields;

  const switchesOf = (row) =>
    stateSwitchesOf(
      {
        states: fields.ordered,
        active: get(row, "places.0.state") || null,
        variables: withRequesterVariables(variables, CONDITIONS),
      },
      { labels: FIELDS, conditionLabels: CONDITIONS },
    );

  const scopeOf = (row) =>
    row.places.some((place) => isNil(place.stateId))
      ? null
      : row.places.map((place) => place.name).join(", ");

  const problemDetail = (problem) => {
    const missing = problem.level === REPORT_LEVELS.UNRESOLVED;
    return (
      <div className="workshop-field-list__detail">
        <div
          className={classNames("workshop-fields__alert", {
            "workshop-fields__alert--warning": !missing,
          })}
        >
          <CanvasIcon path={WORKSHOP_ICONS.warn} size={16} strokeWidth={2} />
          <span className="workshop-fields__alert-text">
            <strong>
              {missing ? FIELDS.UNRESOLVED_TITLE : FIELDS.AMBIGUOUS_TITLE}
            </strong>
            <span>
              {fillTemplate(
                missing ? FIELDS.UNRESOLVED_NOTE : FIELDS.AMBIGUOUS_NOTE,
                { field: problem.field },
              )}
            </span>
          </span>
        </div>
        {problem.reason ? (
          <details className="workshop-field-list__technical">
            <summary>{LIST.TECHNICAL}</summary>
            <code>{problem.reason}</code>
          </details>
        ) : null}
        <div className="workshop-field-list__actions">
          <button
            type="button"
            className="workshop-field-list__cite"
            onClick={() => fields.citeProblem(problem)}
          >
            <CanvasIcon path={WORKSHOP_ICONS.chat} size={16} strokeWidth={2} />
            {LIST.CITE}
          </button>
        </div>
      </div>
    );
  };

  const orphanDetail = (orphan) => (
    <div className="workshop-field-list__detail">
      <span className="workshop-fields__note">{CHECK.ORPHANS_HINT}</span>
      <details className="workshop-field-list__technical">
        <summary>{LIST.TECHNICAL}</summary>
        <code>{orphan.accessor}</code>
      </details>
      <div className="workshop-field-list__actions">
        <button
          type="button"
          className="workshop-fields__orphan-remove"
          disabled={locked}
          onClick={() => answers.onRemove(orphan.accessor)}
        >
          <CanvasIcon path={WORKSHOP_ICONS.trash} size={14} />
          {CHECK.REMOVE_ORPHAN}
        </button>
      </div>
    </div>
  );

  const rowEntry = (row) => ({
    key: row.key,
    icon: SLOT_ICONS[row.slot.type] || WORKSHOP_ICONS.text,
    label: slotLabel(row.slot, FIELDS),
    scope: scopeOf(row),
    summary: rowSummary(row, summaryContext),
    pill: rowPill(row, LIST.PILLS),
    path: technicalPath(row.slot),
    row,
  });

  const problemEntry = (problem) => ({
    key: problem.key,
    icon: WORKSHOP_ICONS.warn,
    label: problem.field,
    scope: null,
    summary:
      problem.level === REPORT_LEVELS.UNRESOLVED
        ? FIELDS.UNRESOLVED_TITLE
        : FIELDS.AMBIGUOUS_TITLE,
    pill: fixPill,
    path: problem.reason,
    detail: () => problemDetail(problem),
  });

  const orphanEntry = (orphan) => ({
    key: `orphan:${orphan.accessor}`,
    icon: WORKSHOP_ICONS.input,
    label: orphan.label || orphan.accessor,
    scope: null,
    summary: CHECK.ORPHANS_HINT,
    pill: fixPill,
    path: orphan.accessor,
    detail: () => orphanDetail(orphan),
  });

  const problemEntries = problems.map(problemEntry);
  const orphanEntries = orphans.map(orphanEntry);
  const rowEntries = new Map(rows.map((row) => [row.key, rowEntry(row)]));
  const opened =
    [
      ...problemEntries,
      ...Array.from(rowEntries.values()),
      ...orphanEntries,
    ].find((entry) => entry.key === fields.open) || null;

  const openedKey = get(opened, "key") || null;
  const stale = !isNil(fields.open) && isNil(opened) && !fields.saving;
  const { select: selectField, guide } = fields;
  const viewKey = guide
    ? [guide.kind, guide.path.join("."), guide.step, guide.zone].join(":")
    : openedKey;
  const activeSlotOf = (row) =>
    get(
      (row.places || []).find((place) => place.key === fields.activeKey),
      "slotId",
      null,
    );

  useEffect(() => {
    if (stale) selectField(null);
  }, [stale, selectField]);

  useEffect(() => {
    if (panelRef.current) panelRef.current.scrollTop = 0;
  }, [viewKey]);

  useEffect(() => {
    const target = {
      [FOCUS.SEARCH]: searchRef,
      [FOCUS.BACK]: backRef.current ? backRef : closeRef,
    }[focusWanted];
    if (isNil(target)) return;
    if (target.current) target.current.focus();
    setFocusWanted(null);
  }, [focusWanted]);

  usePanelMaxHeight(panelRef);

  const openEntry = (entry) => {
    const slot = get(entry, "row.slot");
    const listed =
      get(slot, "type") === SLOT_TYPES.LIST || !isNil(get(slot, "collection"));
    const slotId = listed ? activeSlotOf(entry.row) : null;
    const started = isNil(slotId) ? null : fields.guideFor(slotId);
    if (started) {
      openedFromList.current = guideKeyOf(started);
    } else {
      openedFromList.current = entry.key;
      selectField(entry.key);
    }
    setFocusWanted(FOCUS.BACK);
  };

  const backToList = () => {
    openedFromList.current = null;
    selectField(null);
    setFocusWanted(FOCUS.SEARCH);
  };

  const fromList = guide
    ? openedFromList.current === guideKeyOf(guide)
    : !isNil(openedKey) && openedFromList.current === openedKey;

  const leave = () => (fromList ? backToList() : onClose());

  const chooseSource = async (key) => {
    if (guide.kind === GUIDE_KINDS.LIST && key !== guide.collection) {
      const listRow = fields.rowOfSlot(get(guide, "listSlot.id"));
      if (isNil(listRow)) return;
      const linked = await fields.edit(listRow, { collection: key });
      if (!linked) return;
    }
    fields.setGuide({
      step: GUIDE_STEPS.ZONES,
      zone: 0,
      ...(guide.kind === GUIDE_KINDS.CARD ? { source: key } : {}),
    });
  };

  const centerRecommended = async (on) => {
    const listRow = fields.rowOfSlot(get(guide, "listSlot.id"));
    if (isNil(listRow)) return;
    await fields.edit(listRow, recommendedCenterPatch(on));
  };

  const chooseZone = async (index, choice) => {
    const total = guide.zones.length;
    const slot = guide.zones[index];
    if (!isNil(choice.patch)) {
      const row = fields.rowOfSlot(get(slot, "id"));
      if (isNil(row)) return;
      const saved = await fields.edit(row, choice.patch);
      if (!saved) return;
    }
    fields.setGuide(
      index + 1 < total
        ? { step: GUIDE_STEPS.ZONES, zone: index + 1 }
        : { step: GUIDE_STEPS.DONE },
    );
  };

  const guideTitle = () => {
    if (isNil(guide)) return "";
    if (guide.kind === GUIDE_KINDS.LIST)
      return guide.collection
        ? collectionLabel(guide.collection, GUIDE)
        : GUIDE.LIST_TITLE;
    return get(GUIDE, ["CARD_SOURCES", guide.source]) || GUIDE.CARD_TITLE;
  };

  const sections = [
    { key: SECTIONS.REPORT, title: LIST.REPORT_GROUP, entries: problemEntries },
    ...fieldGroups(fixing ? flagged : rows).map((group) => ({
      key: group.key,
      title: groupTitle(group.key, LIST),
      entries: group.rows.map((row) => rowEntries.get(row.key)),
    })),
    {
      key: SECTIONS.ORPHANS,
      title: fillTemplate(CHECK.ORPHANS, { count: orphans.length }),
      entries: orphanEntries,
    },
  ]
    .map((section) => ({
      ...section,
      entries: section.entries.filter((entry) => matches(entry, query)),
    }))
    .filter((section) => section.entries.length);

  const emptyText = () => {
    if (total === 0) return FIELDS.EMPTY;
    if (fixing && fixCount === 0) return LIST.NOTHING_TO_FIX;
    if (!sections.length) return fillTemplate(LIST.NO_MATCH, { query });
    return null;
  };
  const empty = emptyText();

  const handleKeyDown = (event) => {
    if (event.key !== "Escape") return;
    const typing =
      event.target.closest("input, textarea") &&
      !event.target.closest(".workshop-fields-panel__search");
    if (!typing) onClose();
  };

  const closeButton = (
    <button
      ref={closeRef}
      type="button"
      className="workshop-fields-panel__close"
      aria-label={LIST.CLOSE}
      title={LIST.CLOSE}
      onClick={onClose}
    >
      <CanvasIcon path={WORKSHOP_ICONS.close} size={16} strokeWidth={2} />
    </button>
  );

  const renderHead = ({ icon, title, kind, guided, onBack }) => (
    <div className="workshop-fields-panel__head workshop-fields-panel__head--element">
      {onBack ? (
        <button
          ref={backRef}
          type="button"
          className="workshop-fields-panel__close"
          aria-label={LIST.BACK}
          title={LIST.BACK}
          onClick={onBack}
        >
          <CanvasIcon path={WORKSHOP_ICONS.back} size={16} strokeWidth={2} />
        </button>
      ) : null}
      <span
        className={classNames("workshop-fields-panel__badge", {
          "workshop-fields-panel__badge--guided": guided,
        })}
      >
        <CanvasIcon path={icon} size={18} strokeWidth={1.9} />
      </span>
      <span className="workshop-fields-panel__heading">
        <strong>{title}</strong>
        <span>{kind}</span>
      </span>
      {embedded ? null : closeButton}
    </div>
  );

  const kindOfRow = (row) =>
    `${get(QUICK, ["TYPES", slotKindOf(row.slot)]) || row.slot.type} · ${QUICK.ONE}`;

  const renderDetail = () => {
    const row = opened.row || null;
    const quick = !isNil(row) && QUICK_TYPES.includes(row.slot.type);
    return (
      <>
        {renderHead({
          icon: opened.icon,
          title: opened.label,
          kind: isNil(row) ? opened.pill.text : kindOfRow(row),
          guided: false,
          onBack: fromList ? backToList : null,
        })}
        {quick ? (
          <WorkshopQuickEdit
            key={row.key}
            row={row}
            labels={QUICK}
            fieldLabels={FIELDS}
            checkLabels={CHECK}
            registry={fields.registry}
            known={known}
            actions={actions}
            switches={
              row.slot.type === SLOT_TYPES.ACTION ? switchesOf(row) : []
            }
            variables={variables}
            checkFor={answers.checkFor}
            saving={fields.saving || locked}
            onEdit={fields.edit}
            onCreateField={fields.createField}
            onEditField={fields.editField}
            onUpload={fields.uploadImage}
            onSaveCheck={answers.save}
            onDone={leave}
            onCancel={leave}
          />
        ) : null}
        {isNil(row) ? opened.detail() : null}
        {!isNil(row) && !quick ? (
          <p className="workshop-quick__note">{QUICK.NOTHING}</p>
        ) : null}
      </>
    );
  };

  const renderGuide = () => (
    <>
      {renderHead({
        icon: GUIDE_ICONS[guide.kind],
        title: guideTitle(),
        kind:
          guide.kind === GUIDE_KINDS.LIST ? GUIDE.LIST_KIND : GUIDE.CARD_KIND,
        guided: true,
        onBack: fromList ? backToList : null,
      })}
      <WorkshopGuide
        key={`${guide.kind}:${guide.path.join(".")}:${guide.step}`}
        guide={guide}
        labels={GUIDE}
        fieldLabels={FIELDS}
        collections={collections}
        registry={fields.registry}
        actions={actions}
        disabled={fields.saving || locked}
        onSource={chooseSource}
        onChoose={chooseZone}
        onMove={fields.setGuide}
        onFinish={leave}
        onRecommendedCenter={centerRecommended}
      />
    </>
  );

  const renderSample = () => (
    <section className="workshop-field-list__group workshop-field-list__sample">
      <h3 className="workshop-field-list__group-title">
        {fillTemplate(FIELDS.SAMPLE.ANSWERS, {
          count: sampleFields.answers.length,
        })}
      </h3>
      <span className="workshop-fields__note">
        {sampleFields.answers.length
          ? FIELDS.SAMPLE.ANSWERS_HINT
          : FIELDS.SAMPLE.NO_ANSWERS}
      </span>
      {answersByStep(sampleFields.answers).map((group) => (
        <div key={group.id} className="workshop-sample__step">
          <span className="workshop-sample__step-name">{group.name}</span>
          {group.answers.map((answer) => (
            <SampleAnswer
              key={`${answer.field}:${answerText(answer.value)}`}
              answer={answer}
              field={registryFieldOf(fields.registry, answer.field)}
              disabled={sampleFields.busy}
              onChange={(value) => sampleFields.onAnswer(answer.field, value)}
            />
          ))}
        </div>
      ))}
      {sampleFields.automations.length ? (
        <div className="workshop-sample__step">
          <span className="workshop-sample__step-name">
            {FIELDS.SAMPLE.AUTOMATIONS}
          </span>
          {sampleFields.automations.map((automation, index) => (
            <span
              key={`${automation.connectorKey}:${index}`}
              className={`workshop-sample__automation workshop-sample__automation--${String(
                automation.outcome,
              ).toLowerCase()}`}
            >
              <strong>{automation.connectorKey}</strong>
              <span>
                {`${get(automation, "step.name") || ""} · ${
                  get(FIELDS.SAMPLE.OUTCOMES, automation.outcome) ||
                  automation.outcome
                }${automation.reason ? ` · ${automation.reason}` : ""}`}
              </span>
            </span>
          ))}
        </div>
      ) : null}
      <button
        type="button"
        className="workshop-fields__ask"
        disabled={sampleFields.busy}
        onClick={sampleFields.onReset}
      >
        <CanvasIcon path={WORKSHOP_ICONS.refresh} size={16} />
        {FIELDS.SAMPLE.RESET}
      </button>
    </section>
  );

  const renderList = () => (
    <>
      {embedded ? null : (
        <div className="workshop-fields-panel__head">
          <strong className="workshop-fields-panel__title">
            {VIEWS.FIELDS}
          </strong>
          {closeButton}
        </div>
      )}
      <div className="workshop-field-list__bar">
        <div
          role="group"
          aria-label={LIST.FILTER}
          className="workshop-field-list__filters"
        >
          <button
            type="button"
            aria-pressed={!fixing}
            onClick={() => fields.setFilter(FIELD_FILTERS.ALL)}
          >
            {fillTemplate(LIST.ALL, { count: total })}
          </button>
          <button
            type="button"
            aria-pressed={fixing}
            onClick={() => fields.setFilter(FIELD_FILTERS.FIX)}
          >
            {fillTemplate(LIST.FIX, { count: fixCount })}
          </button>
        </div>
        <button
          type="button"
          className="workshop-fields__detect"
          title={FIELDS.DETECT_TITLE}
          disabled={locked || fields.detecting}
          onClick={fields.detect}
        >
          <CanvasIcon
            path={WORKSHOP_ICONS.refresh}
            size={16}
            strokeWidth={1.9}
          />
          {FIELDS.DETECT}
        </button>
      </div>
      <Input
        ref={searchRef}
        allowClear
        className="workshop-fields-panel__search"
        aria-label={LIST.SEARCH}
        placeholder={LIST.SEARCH}
        prefix={
          <CanvasIcon path={ICON_PATHS.search} size={16} strokeWidth={1.9} />
        }
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {manual.length && !searching ? (
        <div className="canvas-alert canvas-alert--warning">
          <CanvasIcon path={ICON_PATHS.warn} />
          <div>
            <strong>
              {t("CANVAS_EDITOR.STEP.SCREEN.MANUAL_COUNT", {
                count: manual.length,
              })}
            </strong>
            <ul>
              {manual.map((entry, index) => (
                <li key={`${reportEntryText(entry)}-${index}`}>
                  {reportEntryText(entry)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      {empty ? <div className="workshop-field-list__empty">{empty}</div> : null}
      {sections.map((section) => (
        <section key={section.key} className="workshop-field-list__group">
          <h3 className="workshop-field-list__group-title">{section.title}</h3>
          {section.entries.map((entry) => (
            <div key={entry.key} className="workshop-field-list__row">
              <button
                type="button"
                className="workshop-field-list__head"
                onClick={() => openEntry(entry)}
              >
                <FieldSummary entry={entry} />
                <CanvasIcon
                  path={ICON_PATHS.chevronRight}
                  size={16}
                  strokeWidth={2}
                  className="workshop-field-list__chevron"
                />
              </button>
            </div>
          ))}
        </section>
      ))}
      {!fixing && !searching && answers.showStrict ? (
        <section className="workshop-field-list__strict">
          <strong>{CHECK.STRICT.TITLE}</strong>
          <div
            role="group"
            aria-label={CHECK.STRICT.TITLE}
            className="workshop-segmented"
          >
            {[true, false].map((strict) => (
              <button
                key={String(strict)}
                type="button"
                aria-pressed={answers.strict === strict}
                disabled={locked}
                onClick={() =>
                  answers.strict === strict ? null : answers.onStrict(strict)
                }
              >
                {strict ? CHECK.STRICT.BLOCK : CHECK.STRICT.CONTINUE}
              </button>
            ))}
          </div>
          <span className="workshop-fields__note">
            {answers.strict
              ? CHECK.STRICT.BLOCK_HINT
              : CHECK.STRICT.CONTINUE_HINT}
          </span>
        </section>
      ) : null}
      {!fixing && !searching && sampleFields ? renderSample() : null}
    </>
  );

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label={VIEWS.FIELDS}
      tabIndex={-1}
      className={classNames("workshop-field-list workshop-fields-panel", {
        "workshop-fields-panel--embedded": embedded,
      })}
      onKeyDown={handleKeyDown}
    >
      {guide ? renderGuide() : null}
      {!guide && !isNil(opened) ? renderDetail() : null}
      {!guide && isNil(opened) ? renderList() : null}
    </div>
  );
};

ScreenFields.propTypes = {
  workshop: PropTypes.object.isRequired,
  variables: PropTypes.array,
  report: PropTypes.object,
  focusSearch: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  embedded: PropTypes.bool,
};

ScreenFields.defaultProps = {
  variables: [],
  report: null,
  focusSearch: false,
  embedded: false,
};

export default ScreenFields;
